import React, { FC, KeyboardEvent, PointerEvent, useCallback, useEffect, useRef } from 'react';
import { useOurTranslation } from 'app/translations';
import { AnimatedTile } from './animation';
import { boardSize, cellPositions, Direction, hasMoves, winningTile } from './game';
import { useAnimatedGame } from './use-animated-game';
import styles from './styles.sass';
const keyDirections: Partial<Record<string, Direction>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right'
};
const swipeThreshold = 24;
const paletteSize = 11;
const adultCowTile = 16;
const CowTile: FC<{
  tile: AnimatedTile;
}> = ({
  tile: {
    value,
    position,
    appearance
  }
}) => {
  const level = Math.min(Math.log2(value), paletteSize);
  return <div className={styles.tilePosition} data-column={position % boardSize} data-row={Math.floor(position / boardSize)} data-sentry-component="CowTile" data-sentry-source-file="index.tsx">
            <div className={styles.tile} data-level={level} data-appearance={appearance}>
                <span className={styles.cow}>{value < adultCowTile ? '🐮' : '🐄'}</span>
                <strong>{value}</strong>
            </div>
        </div>;
};
const GameHeader: FC<{
  score: number;
  gain: number;
  turn: number;
}> = ({
  score,
  gain,
  turn
}) => {
  const {
    t
  } = useOurTranslation('admin');
  return <div className={styles.header} data-sentry-component="GameHeader" data-sentry-source-file="index.tsx">
            <h2>{t('cow2048Title')}</h2>
            <div className={styles.score}>
                <span>{t('cow2048Score')}</span>
                <output aria-label={t('cow2048Score')}>{score}</output>
                {gain > 0 && <span key={turn} className={styles.scoreGain} aria-hidden>+{gain}</span>}
            </div>
        </div>;
};
const GameStatus: FC<{
  board: Array<number>;
}> = ({
  board
}) => {
  const {
    t
  } = useOurTranslation('admin');
  let status = t('cow2048Instructions');
  if (board.some(tile => tile >= winningTile)) status = t('cow2048Won');
  if (!hasMoves(board)) status = t('cow2048GameOver');
  return <p className={styles.status} role="status" data-sentry-component="GameStatus" data-sentry-source-file="index.tsx">{status}</p>;
};
const GameOver: FC<{
  score: number;
  onRestart(): void;
}> = ({
  score,
  onRestart
}) => {
  const {
    t
  } = useOurTranslation('admin');
  const restartRef = useRef<HTMLButtonElement>(null);
  useEffect(() => restartRef.current?.focus(), []);
  return <div className={styles.gameOver} data-sentry-component="GameOver" data-sentry-source-file="index.tsx">
            <div className={styles.gameOverPanel} role="alert" aria-label={t('cow2048GameOverTitle')}>
                <h3>{t('cow2048GameOverTitle')}</h3>
                <p>{t('cow2048NoMoves')}</p>
                <p className={styles.finalScore}>{t('cow2048FinalScore', {
          score
        })}</p>
                <button ref={restartRef} type="button" className={styles.restart} onClick={onRestart}>
                    {t('cow2048Restart')}
                </button>
            </div>
        </div>;
};

/** A small, local-only game unlocked by the app-info secret code. */
export const Cow2048: FC = () => {
  const {
    t
  } = useOurTranslation('admin');
  const {
    game,
    tiles,
    turn,
    scoreGain,
    move,
    restart,
    phase
  } = useAnimatedGame();
  const boardRef = useRef<HTMLDivElement>(null);
  const pointerStart = useRef<{
    pointerId: number;
    x: number;
    y: number;
  } | null>(null);
  const canMove = hasMoves(game.board);
  useEffect(() => {
    if (canMove) boardRef.current?.focus();
  }, [canMove]);
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>): void => {
    const direction = keyDirections[event.key];
    if (!direction || event.ctrlKey || event.altKey || event.metaKey) return;
    event.preventDefault();
    move(direction);
  }, [move]);
  const handleRestart = useCallback((): void => {
    restart();
    boardRef.current?.focus();
  }, [restart]);
  const handlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>): void => {
    if (pointerStart.current || !canMove) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerStart.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY
    };
    event.currentTarget.focus();
  }, [canMove]);
  const handlePointerEnd = useCallback((event: PointerEvent<HTMLDivElement>): void => {
    const start = pointerStart.current;
    if (!start || start.pointerId !== event.pointerId) return;
    pointerStart.current = null;
    if (event.type === 'pointercancel') return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < swipeThreshold) return;
    const horizontal: Direction = dx > 0 ? 'right' : 'left';
    const vertical: Direction = dy > 0 ? 'down' : 'up';
    move(Math.abs(dx) > Math.abs(dy) ? horizontal : vertical);
  }, [move]);
  return <section className={styles.game} aria-label={t('cow2048Title')} data-sentry-component="Cow2048" data-sentry-source-file="index.tsx">
            <GameHeader score={game.score} gain={scoreGain} turn={turn} data-sentry-element="GameHeader" data-sentry-source-file="index.tsx" />
            <GameStatus board={game.board} data-sentry-element="GameStatus" data-sentry-source-file="index.tsx" />
            <div ref={boardRef} className={styles.board} role="group" aria-label={t('cow2048Board')} tabIndex={0} onKeyDown={handleKeyDown} onPointerDown={handlePointerDown} onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd}>
                {cellPositions.map(position => <div key={position} className={styles.cell} role="img" aria-label={game.board[position] ? t('cow2048Tile', {
        value: game.board[position]
      }) : t('cow2048Empty')} />)}
                <div className={styles.tileLayer} aria-hidden>
                    {tiles.map(tile => <CowTile key={tile.id} tile={tile} />)}
                </div>
                {!canMove && phase === 'idle' && <GameOver score={game.score} onRestart={handleRestart} />}
            </div>
            <p className={styles.help}>{t('cow2048Controls')}</p>
            <div className={styles.footer}>
                <button type="button" className={styles.restart} onClick={handleRestart}>
                    {t('cow2048Restart')}
                </button>
            </div>
        </section>;
};